import { createContext, useState, useContext, ReactNode, useRef } from "react";
import { ModalType, ModalPropsMap } from "./ModalRegistry";

interface ModalState {
	modalVisible: ModalType | undefined;
	modalProps: any;
	hideModal: () => void;
	openModal: <T extends ModalType>(
		modalName: T,
		props?: ModalPropsMap[T]
	) => void;
	registerOpenEvent: (
		handler: (modalName: ModalType, props?: any) => void
	) => () => void;
}

// Create the context
const ModalContext = createContext<ModalState | undefined>(undefined);

// Props interface for the provider
interface ModalStateProviderProps {
	children: ReactNode;
}

// Context provider component
export const ModalStateProvider = ({ children }: ModalStateProviderProps) => {
	const [modalVisible, setModalVisible] = useState<ModalType | undefined>(
		undefined
	);
	const [modalProps, setModalProps] = useState<any>(undefined);
	const handlersRef = useRef<Set<(modalType: ModalType, props?: any) => void>>(
		new Set()
	);

	const registerOpenEvent = (
		handler: (modalType: ModalType, props?: any) => void
	) => {
		handlersRef.current.add(handler);

		// Return cleanup function
		return () => {
			handlersRef.current.delete(handler);
		};
	};

	const openModal = <T extends ModalType>(
		modalType: T,
		props?: ModalPropsMap[T]
	) => {
		handlersRef.current.forEach((handler) => handler(modalType, props));
		setModalVisible(modalType);
		setModalProps(props);
	};

	const hideModal = () => {
		setModalVisible(undefined);
		setModalProps(undefined);
	};

	const value: ModalState = {
		modalVisible,
		modalProps,
		hideModal,
		openModal,
		registerOpenEvent
	};

	return (
		<ModalContext.Provider value={value}>{children}</ModalContext.Provider>
	);
};

// Custom hook to use the tray context
export const useModalState = () => {
	const context = useContext(ModalContext);
	if (!context) {
		throw new Error(
			"useModalState must be used within a <ModalStateProvider> component."
		);
	}
	return context;
};
